/**
 * Accepting a staff invitation.
 *
 * Outside the AppShell and outside the session: the person following this link
 * has an account but no credential, so there is nothing to sign in with yet.
 *
 * The link is validated on load rather than on submit. Someone who follows a
 * three-day-old link should be told immediately, not after they have chosen a
 * password and typed it twice.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';

const MIN_LENGTH = 12;

export function AcceptInviteScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      return;
    }
    api
      .get<{ email: string; name: string }>(`/api/v1/auth/invite/${token}`)
      .then(setInvite)
      .catch(() => setInvalid(true));
  }, [token]);

  if (invalid) {
    return (
      <Centered>
        <h1 className="text-xl font-semibold text-slate-900">{t('invite.invalidTitle')}</h1>
        {/* Deliberately vague about WHY. Expired, revoked, already used and
            never-existed all read the same, so a stale link cannot be used to
            find out whether an account exists. */}
        <p className="mt-2 text-sm text-slate-600">{t('invite.invalidBody')}</p>
      </Centered>
    );
  }

  if (!invite) {
    return (
      <Centered>
        <p className="text-sm text-slate-500">{t('app.loading')}</p>
      </Centered>
    );
  }

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <Centered>
      <h1 className="text-xl font-semibold text-slate-900">
        {t('invite.welcome', { name: invite.name })}
      </h1>
      <p className="mt-1 text-sm text-slate-600">
        {t('invite.chooseFor', { email: invite.email })}
      </p>

      <form
        className="mt-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (password !== confirm || password.length < MIN_LENGTH) return;
          setSubmitting(true);
          setError(null);
          api
            .post(`/api/v1/auth/invite/${token}`, { password })
            // Not signed in automatically: accepting an invitation and starting
            // a session are different acts, and a forwarded link should not
            // become somebody's session.
            .then(() => navigate('/login', { replace: true }))
            .catch((e: Error) => setError(e.message))
            .finally(() => setSubmitting(false));
        }}
      >
        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">{t('invite.password')}</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-slate-600">{t('invite.confirm')}</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
          />
        </label>

        <p className="text-xs text-slate-500">{t('invite.rule', { count: MIN_LENGTH })}</p>

        {tooShort ? (
          <p role="alert" className="text-sm text-red-700">
            {t('invite.tooShort', { count: MIN_LENGTH })}
          </p>
        ) : null}
        {mismatch ? (
          <p role="alert" className="text-sm text-red-700">
            {t('invite.mismatch')}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || tooShort || mismatch || password.length === 0}
          className="w-full rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
        >
          {t('invite.submit')}
        </button>
      </form>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center p-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6">{children}</div>
    </main>
  );
}
